import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { messageBundle, tsx } from '@arcgis/core/widgets/support/widget';
import Widget from '@arcgis/core/widgets/Widget';
import { VNode } from 'src/interfaces/interfaces';
import { EAppTemplateType } from 'templates-common-library/baseClasses/CompatibilityChecker';

import "./AppTypeInfoModal/styles/AppTypeInfoModal.scss";

export interface AppTypeInfoModalProperties extends __esri.WidgetProperties {
  picUrl: string;
  /** Ex: "Media", "Attachment Viewer", ...etc */
  appTypeTitle: string;
  appTypeUrlFragment: string;
  sendFocusOnClose: Function;
}

export const AppType_to_T9NKey = {
  [EAppTemplateType.ThreeDViewer]: "3d",
  [EAppTemplateType.AttachmentViewer]: "av",
  [EAppTemplateType.Basic]: "basic",
  [EAppTemplateType.CategoryGallery]: "catGal",
  [EAppTemplateType.Charts]: "charts",
  [EAppTemplateType.Countdown]: "countdown",
  [EAppTemplateType.Exhibit]: "exhibit",
  [EAppTemplateType.ImageryApp]: "imageView",
  [EAppTemplateType.Insets]: "insets",
  [EAppTemplateType.InteractiveLegend]: "intLeg",
  [EAppTemplateType.Media]: "media",
  [EAppTemplateType.Minimalist]: "minimalist",
  [EAppTemplateType.Nearby]: "nearby",
  [EAppTemplateType.Portfolio]: "portfolio",
  [EAppTemplateType.Sidebar]: "sidebar",
  [EAppTemplateType.Slider]: "slider",
  [EAppTemplateType.ZoneLookup]: "zoneLook"
}

@subclass('Landing.widgets.AppTypeInfoModal')
export default class AppTypeInfoModal extends Widget {

  @property()
  picUrl: string;

  @property()
  appTypeTitle: string;

  @property()
  appTypeUrlFragment: string;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  sendFocusOnClose: Function;

  private _isActive: boolean = true;

  constructor(properties?: AppTypeInfoModalProperties) {
    super(properties);
  }

  render() {
    const { appDescriptions } = this.appMessages;

    const header = this._renderHeader();
    const descSection = this._renderDescription();
    const exSection = this._renderExamples();

    return (
      <calcite-modal
        class="app-description-modal"
        active={this._isActive}
        background-color="white"
        afterCreate={(modal: HTMLCalciteModalElement) => {
          modal.setFocus();
          modal.addEventListener("calciteModalClose", () => {
            this._close();
          });
        }}
        scale="m"
        width="m"
        intl-close={appDescriptions.close}
      >

        { header}

        <div class="app-description-modal__description" slot="content">
          <img class="app-desc-image" src={this.picUrl} width="150px" alt="" />
          {descSection}
          {exSection}
        </div>

        <calcite-button
          slot="primary"
          width="auto"
          scale="l"
          class="app-description-modal__close"
          onclick={() => {
            this._close();
          }}
        >
          {appDescriptions.close}
        </calcite-button>
      </calcite-modal>
    );
  }

  private _close() {
    this._isActive = false;
    this.sendFocusOnClose?.();
    this.destroy();
  }

  private _renderHeader(): VNode {
    return (
      <h2 class="app-description-modal__header" slot="header">
        <div>
          {this.appTypeTitle}
        </div>
      </h2>
    );
  }

  private _renderDescription(): VNode[] {
    const { appDescriptions } = this.appMessages;
    const descKey = `${AppType_to_T9NKey[this.appTypeUrlFragment]}Desc`;

    return [
      <h4>{appDescriptions.sectionTitle}</h4>,
      <p>{appDescriptions[descKey]}</p>
    ];
  }

  private _renderExamples(): VNode[] {
    const { appExamples } = this.appMessages;
    const exKeyRoot = `${AppType_to_T9NKey[this.appTypeUrlFragment]}Ex`;

    let renderList: any[] = Object.keys(appExamples)
      .filter(key => key.indexOf(exKeyRoot) !== -1)
      .map((exKey) => {
        return (
          <li>{appExamples[exKey]}</li>
        )
      });

    return [
      <h4>{appExamples.sectionTitle}</h4>,
      <ul>
        {renderList}
      </ul>
    ];
  }

}
